/**
 * ONLY · header propio.
 * - Pasa a sólido (fondo blanco, texto negro) cuando el disparador «Cambio header»
 *   ([data-on-header-trigger], lo dibuja on-hero) llega al borde inferior del header.
 *   Desde 990 px Horizon scrollea .page-wrapper, no la ventana: se escuchan los dos.
 * - Publica el alto real de la cabecera (--on-chrome-height, para scroll-margin-top)
 *   y los que consumen las secciones de Horizon (--header-height, --header-group-height).
 * - Megamenú desktop: una categoría con links abre su panel al pasar el puntero o al enfocarla;
 *   se cierra al salir (con una pausa corta), con Escape (el foco vuelve a la categoría) o al
 *   tocar un link. Dentro, pasar o enfocar una subcategoría con flecha muestra sus links.
 * - Menú mobile en un <dialog> nativo: Escape lo cierra y el foco vuelve al botón. Los ítems
 *   van como pestañas (ninguna abierta al entrar; tocar la abierta la cierra); en la lista, las
 *   subcategorías con links son acordeones, uno abierto por vez.
 * - Buscador (dialog-component #search-modal de Horizon, se dibuja al final del body): al
 *   cerrarse el foco vuelve al botón BUSCAR. Horizon espera la animación de salida para
 *   llamar a dialog.close(); con Escape el foco sale del panel en el acto (mientras el
 *   <dialog> es modal no se puede enfocar nada afuera) y vuelve al botón con dialog:close.
 *   Si el foco quedó en el body (un toque en una zona sin foco), el Escape no le llega a
 *   Horizon y el navegador cerraba el <dialog> solo, con la página trabada: se cierra con el
 *   cierre de Horizon. Cualquier otro cierre nativo (el gesto «atrás» de Android) suelta el
 *   scroll en el evento close.
 * - Mobile sobre el hero: al pasar a sólido la cabecera sube (sale la barra de anuncio,
 *   on-base.css); el cambio se mide contra su lugar sin subir, así no titila en el borde.
 * - Contador del carrito con el evento estándar de Shopify que usa Horizon.
 * - aria-expanded del botón CARRITO sigue al drawer de Horizon (#cart-drawer): se escuchan
 *   theme-drawer:open/close, que también cubren la apertura automática al agregar.
 * - Velo del carrito (on-cart-drawer.css): desde 990 px lo dibuja el <theme-drawer> y un clic
 *   en él cierra el drawer, como el ::backdrop del modal en mobile.
 * - El carrito se abre sólo cuando la persona agrega algo, nunca al cargar ni al volver a una página:
 *   lleva no-persist (Horizon no lo anota en sessionStorage para reabrirlo en la página siguiente;
 *   lo que haya quedado anotado de antes lo borra on-head) y, si «atrás» trae del bfcache la página
 *   congelada con el carrito abierto, se cierra en pageshow, sin la salida animada.
 * - Anclaje para medir: la marca va sólo en el host (raíz de la banda, sin anidar). En
 *   desktop toma data-section-desktop (la PLP del Figma la llama «content-02», la PDP
 *   «header»); en mobile, «header».
 */
class OnHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('.on-header');
    this.drawer = this.querySelector('[data-on-drawer]');
    this.openButton = this.querySelector('[data-on-drawer-open]');
    this.wrapper = document.querySelector('.page-wrapper');
    this.group = document.getElementById('header-group');
    if (!this.header) return;

    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.desktopQuery.addEventListener('change', this.syncSectionName);
    this.syncSectionName();

    window.addEventListener('scroll', this.update, { passive: true });
    this.wrapper?.addEventListener('scroll', this.update, { passive: true });
    window.addEventListener('resize', this.update);

    this.resizeObserver = new ResizeObserver(this.measure);
    this.resizeObserver.observe(this.header);
    if (this.group) this.resizeObserver.observe(this.group);

    this.openButton?.addEventListener('click', this.openDrawer);
    this.drawer?.addEventListener('click', this.onDrawerClick);
    this.drawer?.addEventListener('close', this.onDrawerClose);

    this.searchButton = this.querySelector('[aria-controls="search-modal"]');
    document.addEventListener('dialog:close', this.restoreSearchFocus, true);
    // close no burbujea: se escucha en captura.
    document.addEventListener('close', this.onSearchClose, true);
    document.addEventListener('keydown', this.onSearchKeydown);
    document.addEventListener('keydown', this.onDocumentKeydown);

    this.cartButton = this.querySelector('[aria-controls="cart-drawer"]');
    document.getElementById('cart-drawer')?.setAttribute('no-persist', '');
    document.addEventListener('theme-drawer:open', this.syncCartExpanded);
    document.addEventListener('theme-drawer:close', this.syncCartExpanded);
    document.addEventListener('click', this.onCartVeilClick);
    this.syncCartExpanded();

    this.megaItems = Array.from(this.querySelectorAll('[data-on-mega]'));
    this.megaItems.forEach((item) => {
      item.addEventListener('mouseenter', this.onMegaEnter);
      item.addEventListener('mouseleave', this.onMegaLeave);
      item.addEventListener('focusin', this.onMegaFocus);
      item.addEventListener('focusout', this.onMegaBlur);
      item.addEventListener('keydown', this.onMegaKeydown);
      item.addEventListener('click', this.onMegaClick);
      item.querySelectorAll('[data-on-mega-cat]').forEach((cat) => {
        cat.addEventListener('mouseenter', this.onMegaCat);
        cat.addEventListener('focus', this.onMegaCat);
      });
    });

    this.listenCart();
    this.update();
    this.measure();
  }

  syncSectionName = () => {
    const desktopName = this.dataset.sectionDesktop || 'header';
    this.setAttribute('data-section', this.desktopQuery.matches ? desktopName : 'header');
  };

  disconnectedCallback() {
    this.desktopQuery?.removeEventListener('change', this.syncSectionName);
    window.removeEventListener('scroll', this.update);
    this.wrapper?.removeEventListener('scroll', this.update);
    window.removeEventListener('resize', this.update);
    this.resizeObserver?.disconnect();
    document.removeEventListener('dialog:close', this.restoreSearchFocus, true);
    document.removeEventListener('close', this.onSearchClose, true);
    document.removeEventListener('keydown', this.onSearchKeydown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('theme-drawer:open', this.syncCartExpanded);
    document.removeEventListener('theme-drawer:close', this.syncCartExpanded);
    document.removeEventListener('click', this.onCartVeilClick);
    if (this.cartEventName) document.removeEventListener(this.cartEventName, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    clearTimeout(this.megaTimer);
  }

  /* ── Megamenú ── */
  openMega(item) {
    clearTimeout(this.megaTimer);
    this.megaItems.forEach((other) => {
      if (other !== item) this.closeMega(other);
    });
    const panel = item.querySelector('[data-on-mega-panel]');
    if (!panel || !panel.hidden) return;
    panel.hidden = false;
    item.querySelector('[data-on-mega-trigger]')?.setAttribute('aria-expanded', 'true');
  }

  closeMega(item) {
    const panel = item.querySelector('[data-on-mega-panel]');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    item.querySelector('[data-on-mega-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  onMegaEnter = (event) => {
    const item = event.currentTarget;
    clearTimeout(this.megaTimer);
    // Pasar de una categoría a otra cambia en el acto; entrar desde afuera espera un instante.
    const anyOpen = this.megaItems.some((other) => !other.querySelector('[data-on-mega-panel]')?.hidden);
    if (anyOpen) this.openMega(item);
    else this.megaTimer = setTimeout(() => this.openMega(item), 80);
  };

  onMegaLeave = (event) => {
    const item = event.currentTarget;
    clearTimeout(this.megaTimer);
    if (item.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) return;
    this.megaTimer = setTimeout(() => this.closeMega(item), 150);
  };

  onMegaFocus = (event) => {
    const item = event.currentTarget;
    if (event.target.matches?.(':focus-visible')) this.openMega(item);
  };

  onMegaBlur = (event) => {
    const item = event.currentTarget;
    if (!item.contains(/** @type {Node} */ (event.relatedTarget))) this.closeMega(item);
  };

  onMegaKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = event.currentTarget;
    const panel = item.querySelector('[data-on-mega-panel]');
    if (!panel || panel.hidden) return;
    event.preventDefault();
    this.closeMega(item);
    item.querySelector('[data-on-mega-trigger]')?.focus({ preventScroll: true });
  };

  onMegaClick = (event) => {
    const link = event.target.closest?.('[data-on-mega-panel] a[href]');
    if (link) this.closeMega(event.currentTarget);
  };

  onMegaCat = (event) => {
    const cat = event.currentTarget;
    const panel = cat.closest('[data-on-mega-panel]');
    if (!panel) return;
    panel.querySelectorAll('[data-on-mega-cat]').forEach((other) => other.classList.toggle('is-active', other === cat));
    const target = cat.getAttribute('aria-controls');
    panel.querySelectorAll('[data-on-mega-group]').forEach((group) => {
      group.hidden = group.id !== target;
    });
  };

  onDocumentKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.megaItems?.forEach((item) => this.closeMega(item));
  };

  update = () => {
    const trigger = document.querySelector('[data-on-header-trigger]');
    if (!trigger || !this.header) return;
    // Sticky con top negativo cuando sube (mobile sólido): se le suma para medir contra su lugar de siempre.
    const lift = this.group ? parseFloat(getComputedStyle(this.group).top) || 0 : 0;
    const solid = trigger.getBoundingClientRect().top <= this.header.getBoundingClientRect().bottom - lift;
    this.header.classList.toggle('is-solid', solid);
  };

  measure = () => {
    if (!this.header) return;
    const headerRect = this.header.getBoundingClientRect();
    const groupTop = this.group ? this.group.getBoundingClientRect().top : headerRect.top;
    const chrome = Math.round(headerRect.bottom - groupTop);
    document.documentElement.style.setProperty('--on-chrome-height', `${chrome}px`);
    document.body.style.setProperty('--header-height', `${Math.round(headerRect.height)}px`);
    document.body.style.setProperty('--header-group-height', `${chrome}px`);
  };

  openDrawer = () => {
    if (!this.drawer || this.drawer.open) return;
    this.drawer.showModal();
    this.openButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
  };

  closeDrawer = () => {
    if (this.drawer?.open) this.drawer.close();
  };

  onDrawerClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[data-on-drawer-tab]');
    if (tab) {
      this.selectTab(tab);
      return;
    }
    const sub = target.closest('[data-on-drawer-sub]');
    if (sub) {
      this.toggleSub(sub);
      return;
    }
    // Click en el fondo (el propio <dialog>, fuera del panel), en el botón de cerrar o en un link.
    if (target === this.drawer || target.closest('[data-on-drawer-close]') || target.closest('a[href]')) {
      this.closeDrawer();
    }
  };

  /** Pestaña del menú mobile: muestra su lista y oculta las demás; tocar la abierta la cierra. */
  selectTab(tab) {
    const open = tab.getAttribute('aria-expanded') !== 'true';
    this.drawer.querySelectorAll('[data-on-drawer-tab]').forEach((other) => {
      const selected = open && other === tab;
      other.setAttribute('aria-expanded', String(selected));
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !selected;
    });
  }

  /** Subcategoría del menú mobile: acordeón, uno abierto por vez dentro de la pestaña. */
  toggleSub(sub) {
    const open = sub.getAttribute('aria-expanded') !== 'true';
    const list = sub.closest('.on-drawer__list');
    list?.querySelectorAll('[data-on-drawer-sub]').forEach((other) => {
      const expanded = open && other === sub;
      other.setAttribute('aria-expanded', String(expanded));
      const panel = document.getElementById(other.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !expanded;
    });
  }

  onDrawerClose = () => {
    this.openButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
  };

  onSearchKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const modal = /** @type {(HTMLElement & { closeDialog?: () => void }) | null} */ (
      document.getElementById('search-modal')
    );
    if (!modal) return;
    const active = /** @type {HTMLElement | null} */ (document.activeElement);
    if (active && modal.contains(active)) {
      active.blur();
      return;
    }
    // Foco afuera del panel: el keydown no pasa por el dialog-component. Sin preventDefault el
    // navegador cierra el <dialog> sin el cierre de Horizon y la página queda con scroll-lock.
    if (!event.defaultPrevented && modal.querySelector('dialog')?.open) {
      event.preventDefault();
      modal.closeDialog?.();
    }
  };

  /** Cierre nativo del buscador (sin closeDialog de Horizon): suelta el scroll que había tomado. */
  onSearchClose = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || dialog.parentElement?.id !== 'search-modal') return;
    import('@theme/utilities')
      .then(({ unlockScroll }) => {
        if (!dialog.open) unlockScroll?.(dialog);
      })
      .catch(() => {});
  };

  restoreSearchFocus = (event) => {
    if (/** @type {HTMLElement} */ (event.target)?.id !== 'search-modal') return;
    const button = this.searchButton;
    if (!button || !button.offsetParent) return;
    const active = document.activeElement;
    if (!active || active === document.body || event.target.contains(active)) {
      button.focus({ preventScroll: true });
    }
  };

  syncCartExpanded = () => {
    const drawer = document.getElementById('cart-drawer');
    this.cartButton?.setAttribute('aria-expanded', String(Boolean(drawer?.hasAttribute('open'))));
  };

  onCartVeilClick = (event) => {
    const drawer = /** @type {HTMLElement & { close?: () => void }} */ (event.target);
    if (drawer?.id === 'cart-drawer' && drawer.localName === 'theme-drawer') drawer.close?.();
  };

  listenCart() {
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        this.cartEventName = StandardEvents?.cartLinesUpdate;
        if (this.cartEventName) document.addEventListener(this.cartEventName, this.onCartUpdate);
      })
      .catch(() => {});
    window.addEventListener('pageshow', this.onPageShow);
  }

  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => this.renderCount(cart?.totalQuantity ?? detail?.itemCount ?? 0))
      .catch(() => {});
  };

  onPageShow = (event) => {
    if (!event.persisted) return;
    this.closeRestoredCart();
    fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => this.renderCount(cart.item_count))
      .catch(() => {});
  };

  /**
   * Página restaurada del bfcache con el carrito abierto (se fue desde el carrito: el link de una
   * línea, Pagar). Se cierra con close() de Horizon (avisa theme-drawer:close, suelta el scroll y
   * devuelve el foco), con la clase «instant» de Horizon para que no se vea salir.
   */
  closeRestoredCart() {
    const drawer = /** @type {(HTMLElement & { close?: () => Promise<void> }) | null} */ (
      document.getElementById('cart-drawer')
    );
    if (drawer?.localName !== 'theme-drawer' || !drawer.hasAttribute('open') || !drawer.close) return;
    const panel = drawer.querySelector('dialog');
    panel?.classList.add('theme-drawer__dialog-instant');
    drawer
      .close()
      .catch(() => {})
      .finally(() => panel?.classList.remove('theme-drawer__dialog-instant'));
  }

  renderCount(count) {
    this.querySelectorAll('[data-on-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
  }
}

if (!customElements.get('on-header')) customElements.define('on-header', OnHeader);
